import React, { useEffect, useState } from 'react';
import {
    FaSearch,
    FaTrophy,
    FaChartLine,
    FaUser,
    FaGlobe,
    FaFlag,
    FaClock,
    FaGamepad,
    FaCrosshairs,
    FaSkull,
    FaHandsHelping,
    FaBullseye,
    FaStar,
    FaMedal,
    FaSteam,
    FaExternalLinkAlt
} from 'react-icons/fa';
import { SiFaceit } from 'react-icons/si';
import "../styles/FaceitFinder.css";
import { useNavigate, useParams } from "react-router-dom";

const FaceitFinder = ({ openModal }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [playerData, setPlayerData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { steamId } = useParams();

    useEffect(() => {
        if (steamId) {
            fetchPlayerData(steamId);
        }
    }, [steamId]);

    const fetchPlayerData = async (identifier) => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(`https://api.faceitfinder.cc/faceit/player?identifier=${identifier}`);
            if (!response.ok) {
                throw new Error('Player not found or server error');
            }
            const data = await response.json();
            setPlayerData(data);
            // Update URL only if it's different from current
            if (steamId !== data.player.steam_id_64) {
                navigate(`/profile/${data.player.steam_id_64}`);
            }
        } catch (err) {
            setError('Failed to fetch player data. Please try again.');
            setPlayerData(null);
        } finally {
            setIsLoading(false);
        }
    };

    const searchPlayer = async (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            await fetchPlayerData(searchQuery);
        }
    };

    const resetAppState = () => {
        setSearchQuery('');
        setPlayerData(null);
        setIsLoading(false);
        setError(null);
        navigate('/'); // Reset URL to the base path
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getMapStats = () => {
        if (!playerData?.summary?.maps) return [];

        const mapCount = {};
        playerData.summary.maps.forEach(map => {
            mapCount[map] = (mapCount[map] || 0) + 1;
        });
        return Object.entries(mapCount).map(([map, count]) => ({
            map: map.replace('de_', '').toUpperCase(),
            count,
            percentage: (count / playerData.summary.maps.length * 100).toFixed(1)
        }));
    };

    // Definicja poziomów z zakresami ELO
    const levels = [
        { level: 10, minElo: 2001, maxElo: Infinity }, // Ustawienie poziomu 10 jako najwyższego
        { level: 9, minElo: 1751, maxElo: 2000 },
        { level: 8, minElo: 1531, maxElo: 1750 },
        { level: 7, minElo: 1351, maxElo: 1530 },
        { level: 6, minElo: 1201, maxElo: 1350 },
        { level: 5, minElo: 1051, maxElo: 1200 },
        { level: 4, minElo: 901, maxElo: 1050 },
        { level: 3, minElo: 751, maxElo: 900 },
        { level: 2, minElo: 501, maxElo: 750 },
        { level: 1, minElo: 100, maxElo: 500 },
    ];

    // Funkcja do określenia aktualnego poziomu i brakującego ELO
    const getLevelInfo = (elo) => {
        for (let i = 0; i < levels.length; i++) {
            const { level, minElo, maxElo } = levels[i];
            if (elo >= minElo && elo <= maxElo) {
                // Jeśli poziom jest 10, brak kolejnego poziomu
                if (level === 10) {
                    return {
                        currentLevel: level,
                        levelRange: `${minElo}+`,
                        remainingElo: null,
                    };
                }
                const nextLevel = levels[i - 1]; // Wyższe poziomy są wcześniej w tablicy
                if (nextLevel) {
                    const requiredElo = nextLevel.minElo;
                    const remaining = requiredElo - elo;
                    return {
                        currentLevel: level,
                        levelRange: `${minElo} - ${maxElo}`,
                        remainingElo: remaining > 0 ? remaining : 0,
                    };
                } else {
                    return {
                        currentLevel: level,
                        levelRange: `${minElo} - ${maxElo}`,
                        remainingElo: null,
                    };
                }
            }
        }
        // Jeśli ELO jest poniżej minimalnego zdefiniowanego poziomu
        return {
            currentLevel: 'Unranked',
            levelRange: `< ${levels[levels.length - 1].minElo}`,
            remainingElo: levels[levels.length - 1].minElo - elo,
        };
    };

    return (
        <div className="faceit-finder-container">
            <div className="background-animation"></div>

            <section className="hero">
                <div className="hero-content">
                    <h1>
                        {/* Call resetAppState on click */}
                        <span
                            className="gradient-text home-link"
                            onClick={resetAppState}
                            style={{ cursor: 'pointer' }}
                        >
                            FaceitFinder
                        </span>
                    </h1>
                    <p className="hero-description">
                        Track FACEIT stats, analyze performance, and explore your detailed CS2 data.
                    </p>
                </div>
            </section>

            <section className="search-section">
                <div className="search-container glass-morphism">
                    <form onSubmit={searchPlayer} className="search-form">
                        <div className="input-wrapper">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Enter SteamID/SteamID64/"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <button type="submit" className="search-button">
                            Search Stats
                        </button>
                    </form>
                </div>
            </section>

            {isLoading && (
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Fetching player statistics...</p>
                </div>
            )}

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {playerData && (
                <>
                    <section className="profile-links">
                        <div className="profile-container glass-morphism">
                            <div className="profile-header">
                                <img src={playerData.player.avatar} alt="Avatar" className="player-avatar" />
                                <div className="profile-info">
                                    <h2 className="profile-name">{playerData.player.nickname}</h2>
                                    <div className="player-details">
                                        <span><FaGlobe /> Region: {playerData.player.cs2.region}</span>
                                        <span><FaFlag /> Country: {playerData.player.country.toUpperCase()}</span>
                                        <span><FaClock /> Member since: {formatDate(playerData.player.activated_at)}</span>
                                    </div>
                                    <div className="matches-info">
                                        <span><FaGamepad /> Statistics from last {playerData.matches.length} matches</span>
                                    </div>
                                </div>
                            </div>
                            <div className="profile-buttons">
                                <a
                                    href={`https://www.faceit.com/en/players/${playerData.player.nickname}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="profile-button faceit"
                                >
                                    <SiFaceit />
                                    FACEIT Profile
                                    <FaExternalLinkAlt className="external-link-icon" />
                                </a>
                                <a
                                    href={`https://steamcommunity.com/profiles/${playerData.player.steam_id_64}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="profile-button steam"
                                >
                                    <FaSteam />
                                    Steam Profile
                                    <FaExternalLinkAlt className="external-link-icon" />
                                </a>
                            </div>
                        </div>
                    </section>

                    <section className="stats">
                        <div className="stats-container">
                            {/* FACEIT Level */}
                            <div className="stat-item glass-morphism">
                                <div className="stat-icon"><FaTrophy /></div>
                                <div className="stat-number">
                                    {(() => {
                                        const elo = playerData.player.cs2.faceit_elo;
                                        const levelInfo = getLevelInfo(elo);
                                        return (
                                            <>
                                                {levelInfo.currentLevel}
                                                <span className="level-range"> ({levelInfo.levelRange}) Point range</span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <p>FACEIT Level</p>
                            </div>

                            {/* ELO Rating */}
                            <div className="stat-item glass-morphism">
                                <div className="stat-icon"><FaChartLine /></div>
                                <div className="stat-number">
                                    {playerData.player.cs2.faceit_elo}
                                    {(() => {
                                        const elo = playerData.player.cs2.faceit_elo;
                                        const levelInfo = getLevelInfo(elo);
                                        return (
                                            <>
                                                {levelInfo.remainingElo !== null && (
                                                    <span className="remaining-elo">
                                                        {levelInfo.remainingElo} ELO until next level
                                                    </span>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                                <p>ELO Rating</p>
                            </div>
                        </div>
                    </section>

                    <section className="detailed-stats">
                        <div className="stats-grid">
                            {/* Combat Stats */}
                            <div className="stats-card glass-morphism">
                                <h3>Combat Statistics</h3>
                                <div className="performance-stats">
                                    <div className="stat-row">
                                        <span><FaCrosshairs /> Kills</span>
                                        <span className="highlight">{playerData.summary.totalKills}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaSkull /> Deaths</span>
                                        <span className="highlight">{playerData.summary.totalDeaths}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaHandsHelping /> Assists</span>
                                        <span className="highlight">{playerData.summary.totalAssists}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaBullseye /> Headshots</span>
                                        <span className="highlight">{playerData.summary.totalHeadshots}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaStar /> MVPs</span>
                                        <span className="highlight">{playerData.summary.totalMVPs}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Performance Metrics */}
                            <div className="stats-card glass-morphism">
                                <h3>Performance Metrics</h3>
                                <div className="additional-stats">
                                    <div className="stat-row">
                                        <span>K/D Ratio</span>
                                        <span className="highlight">{playerData.summary.averageKDRatio.toFixed(2)}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span>Headshot %</span>
                                        <span className="highlight">{playerData.summary.averageHeadshotsPercent.toFixed(2)}%</span>
                                    </div>
                                    <div className="stat-row">
                                        <span>K/R Ratio</span>
                                        <span className="highlight">{playerData.summary.averageKRRatio.toFixed(2)}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span>Avg Score</span>
                                        <span className="highlight">{playerData.summary.averageFinalScore.toFixed(2)}</span>
                                    </div>
                                    {/* Przeniesienie Total Rounds tutaj */}
                                    <div className="stat-row">
                                        <span>Total Rounds</span>
                                        <span className="highlight">{playerData.summary.totalRounds}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Multi-Kill Achievements */}
                            <div className="stats-card glass-morphism">
                                <h3>Multi-Kill Achievements</h3>
                                <div className="performance-stats">
                                    <div className="stat-row">
                                        <span><FaMedal /> Double Kills</span>
                                        <span className="highlight">{playerData.summary.totalDoubleKills}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaMedal /> Triple Kills</span>
                                        <span className="highlight">{playerData.summary.totalTripleKills}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaMedal /> Quadro Kills</span>
                                        <span className="highlight">{playerData.summary.totalQuadroKills}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span><FaMedal /> Penta Kills</span>
                                        <span className="highlight">{playerData.summary.totalPentaKills}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Map Statistics */}
                            <div className="stats-card glass-morphism">
                                <h3>Map Statistics</h3>
                                <div className="map-stats">
                                    {getMapStats().map(({ map, count, percentage }) => (
                                        <div key={map} className="map-stat-row">
                                            <div className="map-header">
                                                <span className="map-name">{map}</span>
                                                <span className="win-rate-text">{count} matches ({percentage}%)</span>
                                            </div>
                                            <div className="win-rate-bar">
                                                <div
                                                    className="win-rate-fill"
                                                    style={{ width: `${percentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </div>
    );

};

export default FaceitFinder;
